import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Inbox, ArrowUpDown, ArrowUp, ArrowDown, CalendarIcon } from "lucide-react";
import { useState, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n, intlTag } from "@/i18n";

// Nur Darstellung: interne Statuscodes bleiben unverändert, das Label kommt aus `status.<code>`.
const STATUS_CLS = {
  entwurf: "bg-muted text-muted-foreground border-border",
  bestaetigt: "bg-chart-4/15 text-chart-4 border-chart-4/30",
  teilweise_versandt: "bg-chart-3/20 text-chart-3 border-chart-3/40",
  versandt: "bg-primary/15 text-primary border-primary/30",
  geliefert: "bg-primary/15 text-primary border-primary/30",
  storniert: "bg-destructive/15 text-destructive border-destructive/30",
  offen: "bg-chart-3/20 text-chart-3 border-chart-3/40",
  teilweise: "bg-chart-3/20 text-chart-3 border-chart-3/40",
  bezahlt: "bg-primary/15 text-primary border-primary/30",
  erstattet: "bg-destructive/15 text-destructive border-destructive/30",
  gepickt: "bg-chart-4/15 text-chart-4 border-chart-4/30",
  abgeschlossen: "bg-primary/15 text-primary border-primary/30",
};

export function StatusBadge({ status, testId }) {
  const { t } = useI18n();
  const cls = STATUS_CLS[status] || "bg-muted text-muted-foreground border-border";
  return (
    <span data-testid={testId}
      className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold", cls)}>
      {t(`status.${status}`, status)}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions, compact = false }) {
  return (
    <div className={`flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between ${compact ? "mb-4" : "mb-6"}`}>
      <div>
        <h1 className={compact
          ? "font-heading text-xl font-semibold tracking-tight"
          : "font-heading text-3xl md:text-4xl font-semibold tracking-tight"}>{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, hint, action, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center rounded-md border border-dashed border-border bg-card/50 py-16 px-6 text-center">
      <div className="rounded-full bg-muted p-4 mb-4"><Icon className="h-7 w-7 text-muted-foreground" /></div>
      <h3 className="font-heading text-lg font-medium">{title}</h3>
      {hint && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function KpiCard({ label, value, sub, icon: Icon, tone = "default", testId, index = 0 }) {
  const tones = {
    default: "text-foreground",
    primary: "text-primary",
    warn: "text-chart-3",
    danger: "text-destructive",
  };
  return (
    <motion.div data-testid={testId}
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      className="rounded-md border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className={cn("mt-3 font-mono text-2xl font-semibold tracking-tight", tones[tone])}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </motion.div>
  );
}

export function Th({ children, right, sticky, title }) {
  // Rechtsbündige Spalten sind Zahlenspalten: nie umbrechen, damit z. B. „1.039,84 €" nicht
  // zwischen Betrag und Währungszeichen zerreißt.
  return <th title={title || undefined} className={cn("px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground", right ? "text-right whitespace-nowrap" : "text-left", sticky && "sticky top-0 z-10 bg-muted")}>{children}</th>;
}
export function Td({ children, right, mono, className, ...rest }) {
  // `tabular-nums` gibt allen Ziffern die gleiche Breite -> Beträge stehen spaltenweise exakt
  // untereinander und sind nebeneinander unterscheidbar. `whitespace-nowrap` verhindert den
  // Umbruch vor dem €-Zeichen. `pl-6` schafft Luft zwischen benachbarten Zahlenspalten.
  return <td className={cn("px-3 py-2.5 text-sm border-t border-border", right && "text-right whitespace-nowrap tabular-nums pl-6", mono && "font-mono", className)} {...rest}>{children}</td>;
}

export function useSortable(items, accessors, initialKey = null, initialDir = "asc") {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);
  const toggle = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const sorted = useMemo(() => {
    const arr = [...(items || [])];
    if (!sortKey || !accessors[sortKey]) return arr;
    const acc = accessors[sortKey];
    arr.sort((a, b) => {
      let x = acc(a), y = acc(b);
      x = x == null ? "" : x; y = y == null ? "" : y;
      if (typeof x === "number" && typeof y === "number") return sortDir === "asc" ? x - y : y - x;
      return sortDir === "asc"
        ? String(x).localeCompare(String(y), "de", { numeric: true })
        : String(y).localeCompare(String(x), "de", { numeric: true });
    });
    return arr;
  }, [items, sortKey, sortDir, accessors]);
  return { sorted, sortKey, sortDir, toggle };
}

export function parseLocaleNumber(str, localeDec) {
  let s = String(str == null ? "" : str).trim().replace(/\s/g, "");
  if (!s || s === "-") return NaN;
  s = s.replace(/[^0-9.,-]/g, "");
  const hasC = s.includes(","), hasD = s.includes(".");
  if (hasC && hasD) {
    const dec = s.lastIndexOf(",") > s.lastIndexOf(".") ? "," : ".";
    const thou = dec === "," ? "." : ",";
    s = s.split(thou).join("").replace(dec, ".");
  } else if (hasC || hasD) {
    const sep = hasC ? "," : ".";
    if (sep === localeDec) s = s.replace(sep, ".");
    else s = s.split(sep).join("");
  }
  const n = parseFloat(s);
  return isNaN(n) ? NaN : n;
}

export function DateInput({ value, onChange, className, testId, disabled }) {
  const { locale } = useI18n();
  // Nativer Datepicker (verlässliche Eingabe), zusätzlich die Anzeige im Format der App-Sprache,
  // damit in deutscher UI kein mm/dd/yyyy fehlinterpretiert wird.
  return (
    <div className={className}>
      <Input type="date" value={value || ""} disabled={disabled} lang={intlTag(locale)}
        data-testid={testId} onChange={(e) => onChange(e.target.value)} />
      {value && (
        <div className="mt-1 text-xs text-muted-foreground" data-testid={testId ? `${testId}-formatted` : undefined}>
          {new Date(value).toLocaleDateString(intlTag(locale),
            { day: "2-digit", month: "2-digit", year: "numeric" })}
        </div>
      )}
    </div>
  );
}

export function NumberInput({ value, onChange, decimals, className, placeholder, testId, disabled, step }) {
  const [focused, setFocused] = useState(false);
  const [buf, setBuf] = useState("");
  const { locale } = useI18n();
  // Dezimaltrennzeichen folgt der APP-Sprache, nicht der Browser-Locale: sonst wird "6,25" bei
  // englischem Browser als Tausendertrennung gelesen und zu 625.
  const tag = intlTag(locale);
  const localeDec = useMemo(() => (1.1).toLocaleString(tag).replace(/[0-9]/g, "") || ".", [tag]);
  const isEmpty = value === "" || value == null || isNaN(Number(value));
  const formatted = isEmpty ? "" : Number(value).toLocaleString(tag,
    decimals != null ? { minimumFractionDigits: 0, maximumFractionDigits: decimals } : { maximumFractionDigits: 6 });
  const display = focused ? buf : formatted;
  return (
    <input
      type="text" inputMode="decimal" disabled={disabled} data-testid={testId} placeholder={placeholder}
      className={cn("flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", className)}
      value={display}
      onFocus={() => { setFocused(true); setBuf(isEmpty ? "" : String(value).replace(".", localeDec)); }}
      onBlur={() => setFocused(false)}
      onChange={(e) => { const raw = e.target.value; setBuf(raw); const n = parseLocaleNumber(raw, localeDec); onChange(isNaN(n) ? "" : n); }}
    />
  );
}

export function DatePicker({ value, onChange, testId, className, placeholder }) {
  const { locale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const date = value ? new Date(value.length <= 10 ? value + "T00:00:00" : value) : undefined;
  const valid = date && !isNaN(date.getTime());
  // Anzeige folgt immer der App-Locale (nicht der Browser-Locale)
  const label = valid
    ? new Intl.DateTimeFormat(intlTag(locale), { day: "2-digit", month: "2-digit", year: "numeric" }).format(date)
    : (placeholder || t("common.datePlaceholder"));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testId}
          className={cn("justify-start text-left font-normal", !valid && "text-muted-foreground", className)}>
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />{label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={valid ? date : undefined} defaultMonth={valid ? date : undefined}
          onSelect={(d) => { if (d) { onChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`); } setOpen(false); }}
          initialFocus />
      </PopoverContent>
    </Popover>
  );
}

export function SortTh({ children, right, sortId, active, dir, onSort, sticky }) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th onClick={() => onSort(sortId)} data-testid={`sort-${sortId}`}
      className={cn("px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors", right ? "text-right" : "text-left", sticky && "sticky top-0 z-10 bg-muted")}>
      <span className={cn("inline-flex items-center gap-1", right && "flex-row-reverse")}>{children}<Icon className={cn("h-3 w-3", active ? "text-primary" : "opacity-40")} /></span>
    </th>
  );
}
